/**
 * Das Beleg-Modell: ein EINGEFRORENES Datenobjekt, aus dem PDF (und spaeter
 * XML) projiziert werden. Es liegt als JSONB neben den PDF-Bytes in
 * `documents.daten` und wird nie neu berechnet.
 *
 * Herkunft: int1183_bbd `packages/types/src/beleg.ts` (origin/main, Stand
 * 2026-10-15), angepasst:
 *  - Betraege BRUTTO-fuehrend (Shop liefert brutto), netto/steuer je Position
 *    aus der Gruppenverteilung (steuer.ts)
 *  - Belegarten rechnung | gutschrift | lieferschein (kein Angebot, keine AB)
 *  - Steuerfaelle inland | eu_oss | eu_igl | drittland
 *  - kein Gliederungs-/Sorten-/Angebotsblock (Druckkalkulation, hier ohne Nutzen)
 *  - `ist_test` + Praefix TEST- (Ruling R4)
 */
import { z } from 'zod';
import { adresseSchema } from './adresse.js';
import { STEUERFAELLE, STEUERKATEGORIEN } from './steuer.js';
import { BELEG_ARTEN } from './entitaeten.js';

export const parteiSchema = z.object({
  firma: z.string().nullable(),
  name: z.string().min(1),
  strasse: z.string().min(1),
  hausnummer: z.string(),
  plz: z.string().min(1),
  ort: z.string().min(1),
  land: z.string().regex(/^[A-Z]{2}$/),
  email: z.string().nullable(),
  telefon: z.string().nullable(),
  ust_id: z.string().nullable(),
  steuernummer: z.string().nullable(),
  register: z.string().nullable(),
  geschaeftsfuehrung: z.string().nullable(),
  kleinunternehmer: z.boolean().default(false),
});
export type Partei = z.infer<typeof parteiSchema>;

export const belegAdresseSchema = adresseSchema.pick({
  vorname: true, nachname: true, firma: true, strasse: true, hausnummer: true, zusatz: true, plz: true, ort: true, land: true,
});
export type BelegAdresse = z.infer<typeof belegAdresseSchema>;

export const befreiungSchema = z.object({
  code: z.enum(['VATEX-EU-IC', 'VATEX-EU-G']),
  text: z.string().min(1),
});

export const belegSteuergruppeSchema = z.object({
  steuersatz: z.number(),
  kategorie: z.enum(STEUERKATEGORIEN),
  brutto_cents: z.number().int(),
  netto_cents: z.number().int(),
  steuer_cents: z.number().int(),
});
export type BelegSteuergruppe = z.infer<typeof belegSteuergruppeSchema>;

export const BELEG_POSITION_ARTEN = ['artikel', 'versand', 'rabatt', 'erstattung'] as const;

export const belegPositionSchema = z.object({
  pos: z.number().int().positive(),
  art: z.enum(BELEG_POSITION_ARTEN),
  sku: z.string().nullable(),
  bezeichnung: z.string().min(1),
  beschreibung: z.array(z.string()),
  menge: z.number(),
  einheit: z.enum(['Stück', 'Pauschale']),
  einzel_brutto_cents: z.number().int(),
  gesamt_brutto_cents: z.number().int(),
  netto_cents: z.number().int(),
  steuer_cents: z.number().int(),
  steuersatz: z.number(),
  steuerkategorie: z.enum(STEUERKATEGORIEN),
  /** Bezug auf die Auftragsposition (nicht auf dem Druck). */
  order_item_id: z.string().nullable(),
  /**
   * Nur Artikel: Woo-Rabatt dieser Position (brutto, R105). Die Position bleibt VOR Rabatt ausgewiesen, der Rabatt
   * steht steuerlich in den Rabattpositionen je Satz; der Druck zeigt ihn wie Billbee als Prozent in der Zeile.
   */
  rabatt_brutto_cents: z.number().int().default(0),
  /** Nur Rabattposition: schon je Artikelzeile als Prozent gedruckt, deshalb keine eigene Tabellenzeile (R105). */
  in_positionen: z.boolean().default(false),
});
export type BelegPosition = z.infer<typeof belegPositionSchema>;

export const zahlungsblockSchema = z.discriminatedUnion('variante', [
  z.object({ variante: z.literal('bezahlt'), bezahlt_am: z.string(), zahlart: z.string(), referenz: z.string().nullable() }),
  z.object({ variante: z.literal('erstattet'), erstattet_am: z.string().nullable(), zahlart: z.string(), referenz: z.string().nullable() }),
  z.object({ variante: z.literal('keine') }),
]);

export const belegSchema = z.object({
  art: z.enum(BELEG_ARTEN),
  nummer: z.string().min(1),
  ist_test: z.boolean(),
  datum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  leistungsdatum: z.string().nullable(),
  aussteller: parteiSchema,
  marke: z.object({ code: z.string(), name: z.string() }),
  empfaenger: z.object({
    rechnung: belegAdresseSchema,
    lieferung: belegAdresseSchema.nullable(),
    email: z.string().nullable(),
    kundennummer: z.string().nullable(),
    ust_id: z.string().nullable(),
  }),
  positionen: z.array(belegPositionSchema).min(1),
  steuer: z.object({
    fall: z.enum(STEUERFAELLE),
    lieferland: z.string(),
    gruppen: z.array(belegSteuergruppeSchema),
    befreiung: befreiungSchema.nullable(),
    /** Hinweistext auf dem Beleg (z. B. OSS: „Umsatzsteuer nach § 18j UStG, Besteuerung im Bestimmungsland"). */
    hinweis: z.string().nullable(),
  }),
  summen: z.object({
    netto_cents: z.number().int(),
    steuer_cents: z.number().int(),
    brutto_cents: z.number().int(),
    rabatt_brutto_cents: z.number().int(),
  }),
  zahlung: zahlungsblockSchema,
  referenzen: z.object({
    bestellnummer: z.string(),
    sendung: z.string().nullable(),
    bezug_beleg: z.object({ nummer: z.string(), datum: z.string() }).nullable(),
    /** Bestelldatum (Kalendertag), Feldliste wie Billbee (R105). Aeltere Belege: fehlt. */
    bestelldatum: z.string().nullable().default(null),
  }),
  /** Lieferschein als Geschenk: ohne Preise und ohne Rechnungsbezug (R105). */
  geschenk: z.boolean().default(false),
  /** Lieferschein: ohne Preise (Geschenk). */
  ohne_preise: z.boolean().default(false),
  texte: z.object({
    einleitung: z.string().nullable(),
    schluss: z.string().nullable(),
    hinweise: z.array(z.string()),
  }),
}).superRefine((b, ctx) => {
  const gruppenBrutto = b.steuer.gruppen.reduce((s, g) => s + g.brutto_cents, 0);
  const posBrutto = b.positionen.reduce((s, p) => s + p.gesamt_brutto_cents, 0);
  if (b.art !== 'lieferschein' && gruppenBrutto !== posBrutto) {
    ctx.addIssue({ code: 'custom', message: `Summe Positionen (${posBrutto}) ≠ Summe Gruppen (${gruppenBrutto})` });
  }
  if (b.art !== 'lieferschein' && b.summen.brutto_cents !== b.summen.netto_cents + b.summen.steuer_cents) {
    ctx.addIssue({ code: 'custom', message: 'brutto ≠ netto + steuer' });
  }
  if (b.art === 'gutschrift' && !b.referenzen.bezug_beleg) {
    ctx.addIssue({ code: 'custom', message: 'Gutschrift braucht einen Bezug auf die Rechnung' });
  }
  if (b.ist_test !== b.nummer.startsWith('TEST-')) {
    ctx.addIssue({ code: 'custom', message: 'ist_test und TEST-Praefix der Nummer muessen zusammenpassen' });
  }
});
export type Beleg = z.infer<typeof belegSchema>;

/** Belegnummer im Jahresformat: [TEST-]<Kreis>-<Jahr>-<5 Stellen>, z. B. RE-2026-00001, TEST-RE-2026-00001. */
export function belegnummer(kreis: 'RE' | 'GS' | 'LS', jahr: number, laufnummer: number, istTest: boolean): string {
  return `${istTest ? 'TEST-' : ''}${kreis}-${jahr}-${String(laufnummer).padStart(5, '0')}`;
}

/**
 * Belegnummer im fortlaufenden Format wie Billbee (R105): [TEST-]<Praefix><Laufnummer>, ohne Jahr, ohne Nullen,
 * z. B. 68100 (Rechnung), GS-12 (Gutschrift), TEST-68100. Muss zu `beleg_nummer_fortlaufend` (Migration 420) passen.
 */
export function belegnummerFortlaufend(praefix: string, laufnummer: number, istTest: boolean): string {
  return `${istTest ? 'TEST-' : ''}${praefix}${laufnummer}`;
}

/** Formate eines Nummernkreises (Tabelle nummernkreis_formate). Ohne Zeile gilt `jahr`. */
export const NUMMERNKREIS_FORMATE = ['jahr', 'fortlaufend'] as const;
export type NummernkreisFormat = (typeof NUMMERNKREIS_FORMATE)[number];
